import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  ClipboardList, Plus, Loader2, CheckCircle, Clock, AlertTriangle, 
  User, Briefcase, Trash2, ChevronDown, ChevronRight, Edit2,
  Calendar, CircleDot, ChevronLeft, FileText, Send, XCircle, BarChart3
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const departments = [
  { id: 'all', label: 'All Departments', color: 'bg-gray-500' },
  { id: 'pm', label: 'Project Manager', color: 'bg-blue-500' },
  { id: 'hr', label: 'HR', color: 'bg-pink-500' },
  { id: 'finance', label: 'Finance', color: 'bg-emerald-500' },
  { id: 'sales', label: 'Sales', color: 'bg-amber-500' },
  { id: 'marketing', label: 'Marketing', color: 'bg-purple-500' },
  { id: 'operation', label: 'Operation', color: 'bg-cyan-500' },
  { id: 'creative', label: 'Creative', color: 'bg-rose-500' },
];

const pointStatuses = {}; // Removed - status is implicit by section (done/doing/blockers)

const standupStatuses = {
  in_progress: { label: 'In Progress', icon: Clock, bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  completed: { label: 'Completed', icon: CheckCircle, bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  pending: { label: 'Pending', icon: CircleDot, bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
};

const getWeekInfo = (dateStr) => Math.ceil(new Date(dateStr).getDate() / 7);
const getWeekLabel = (weekNum, month, year) => {
  const startDay = (weekNum - 1) * 7 + 1;
  const endDay = Math.min(weekNum * 7, new Date(year, month, 0).getDate());
  const mn = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `Week ${weekNum} (${startDay}-${endDay} ${mn[month - 1]})`;
};

// Helper: normalize field to points array (backward compat for old string format)
const toPointsArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val.trim()) return [{ id: 'legacy', text: val, status: 'pending' }];
  return [];
};

// Points input component
function PointsInput({ points, onChange, color, placeholder }) {
  const addPoint = () => onChange([...points, { id: `new-${Date.now()}`, text: '', status: 'pending' }]);
  const updateText = (idx, text) => { const p = [...points]; p[idx] = { ...p[idx], text }; onChange(p); };
  const removeNew = (idx) => { if (points[idx]?.id?.startsWith('new-')) onChange(points.filter((_, i) => i !== idx)); };
  return (
    <div className="space-y-1.5">
      {points.map((p, i) => (
        <div key={p.id || i} className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
          <Input
            value={p.text}
            onChange={(e) => updateText(i, e.target.value)}
            placeholder={placeholder}
            className="h-8 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white flex-1"
          />
          {p.id?.startsWith('new-') && (
            <button onClick={() => removeNew(i)} className="text-red-400/50 hover:text-red-400 shrink-0"><Trash2 size={12} /></button>
          )}
        </div>
      ))}
      <button onClick={addPoint} className="flex items-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded hover:bg-white/5 transition-colors" style={{ color }}>
        <Plus size={11} /> Add point
      </button>
    </div>
  );
}

// Points display — simple bullet list
function PointsDisplay({ points, color, emptyText }) {
  const arr = toPointsArray(points);
  if (arr.length === 0) return <p className="text-[11px] text-[#6b7280] italic">{emptyText}</p>;

  return (
    <div className="space-y-1">
      {arr.map((p, i) => (
        <div key={p.id || i} className="flex items-start gap-2 py-0.5">
          <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: color }} />
          <span className="text-[11px] leading-relaxed text-[#d1d5db]">{p.text}</span>
        </div>
      ))}
    </div>
  );
}

export default function StandupPage() {
  const [standups, setStandups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ done: [{ id: 'new-1', text: '' }], doing: [{ id: 'new-2', text: '' }], blockers: [], department: '' });
  const [selectedDept, setSelectedDept] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportWeek, setReportWeek] = useState('all');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/standups`, { headers });
      setStandups(res.data.standups);
      setExpandedWeeks({ [getWeekInfo(new Date().toISOString())]: true });
    } catch { toast.error('Failed to load standups'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    const donePoints = form.done.filter(p => p.text.trim());
    const doingPoints = form.doing.filter(p => p.text.trim());
    if (donePoints.length === 0 && doingPoints.length === 0) { toast.error('Sila isi sekurang-kurangnya 1 point'); return; }
    if (!form.department) { toast.error('Sila pilih department'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/standups`, {
        done: donePoints, doing: doingPoints,
        blockers: form.blockers.filter(p => p.text.trim()),
        department: form.department
      }, { headers });
      setStandups([res.data.standup, ...standups]);
      setShowAdd(false);
      setForm({ done: [{ id: 'new-1', text: '' }], doing: [{ id: 'new-2', text: '' }], blockers: [], department: '' });
      toast.success('Standup posted!');
    } catch { toast.error('Failed to post'); }
    finally { setSubmitting(false); }
  };

  const handleUpdatePoints = async (standupId, field, newPoints) => {
    try {
      await axios.put(`${API}/standups/${standupId}`, { [field]: newPoints }, { headers });
      setStandups(standups.map(s => s.id === standupId ? { ...s, [field]: newPoints } : s));
    } catch { toast.error('Failed to update'); }
  };

  const handleUpdateStatus = async (standupId, newStatus) => {
    try {
      await axios.put(`${API}/standups/${standupId}`, { status: newStatus }, { headers });
      setStandups(standups.map(s => s.id === standupId ? { ...s, status: newStatus } : s));
      toast.success('Status updated');
    } catch { toast.error('Failed to update'); }
  };

  const canManage = (s) => s.user_id === currentUser.id || ['admin', 'developer', 'manager'].includes(currentUser.role_id);

  const filteredStandups = useMemo(() => standups.filter(s => {
    const deptMatch = selectedDept === 'all' || s.department === selectedDept;
    const d = new Date(s.created_at);
    return deptMatch && d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
  }), [standups, selectedDept, selectedMonth, selectedYear]);

  const weeklyGroups = useMemo(() => {
    const groups = {};
    filteredStandups.forEach(s => { const w = getWeekInfo(s.created_at); if (!groups[w]) groups[w] = []; groups[w].push(s); });
    return Object.entries(groups).sort(([a], [b]) => Number(b) - Number(a));
  }, [filteredStandups]);

  const stats = useMemo(() => {
    let totalPoints = 0;
    filteredStandups.forEach(s => {
      ['done', 'doing', 'blockers'].forEach(f => { totalPoints += toPointsArray(s[f]).length; });
    });
    return {
      total: filteredStandups.length,
      members: new Set(filteredStandups.map(s => s.user_id)).size,
      totalPoints,
    };
  }, [filteredStandups]);

  const getDeptInfo = (deptId) => departments.find(d => d.id === deptId) || departments[0];
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  if (loading) return <ResponsiveLayout><div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div></ResponsiveLayout>;

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader
          icon={ClipboardList}
          title="Weekly Standup"
          subtitle="Share your progress and blockers"
          iconBg="bg-gradient-to-br from-indigo-500 to-violet-600 shadow-indigo-500/20"
          action={
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setReportOpen(true)} className="border-indigo-500/40 text-indigo-400 hover:bg-indigo-500/10" data-testid="standup-report-btn">
                <BarChart3 size={14} className="mr-1.5" />Report
              </Button>
              <Button onClick={() => setShowAdd(true)} className="bg-gradient-to-r from-indigo-500 to-violet-600 text-white" data-testid="add-standup-btn">
                <Plus size={16} className="mr-1.5" />Post Update
              </Button>
            </div>
          }
        />

        {/* Month Nav + Stats */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg px-2 py-1">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { if (selectedMonth === 0) { setSelectedMonth(11); setSelectedYear(y => y - 1); } else setSelectedMonth(m => m - 1); }}><ChevronLeft size={14} /></Button>
            <span className="text-xs sm:text-sm font-semibold min-w-[120px] sm:min-w-[140px] text-center">{monthNames[selectedMonth]} {selectedYear}</span>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { if (selectedMonth === 11) { setSelectedMonth(0); setSelectedYear(y => y + 1); } else setSelectedMonth(m => m + 1); }}><ChevronRight size={14} /></Button>
          </div>
          <div className="flex gap-3 flex-wrap text-xs">
            <span className="text-[hsl(var(--text-muted))]"><span className="font-bold text-white">{stats.total}</span> updates</span>
            <span className="text-[#8b5cf6]">{stats.totalPoints} points</span>
            <span className="text-[hsl(var(--text-muted))]"><User size={10} className="inline mr-0.5" />{stats.members} members</span>
          </div>
        </div>

        {/* Department Filter */}
        <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1">
          {departments.map(dept => (
            <Button key={dept.id} variant={selectedDept === dept.id ? 'default' : 'outline'} size="sm"
              onClick={() => setSelectedDept(dept.id)}
              className={`shrink-0 text-xs ${selectedDept === dept.id ? 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white border-0' : 'border-[hsl(var(--border))] text-[hsl(var(--text-secondary))]'}`}
            >
              {dept.id !== 'all' && <span className={`w-1.5 h-1.5 rounded-full ${dept.color} mr-1.5`} />}
              {dept.label}
            </Button>
          ))}
        </div>

        {/* Weekly Groups */}
        <div className="space-y-4">
          {weeklyGroups.length === 0 ? (
            <div className="text-center py-16 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl">
              <ClipboardList size={40} className="mx-auto text-indigo-500/30 mb-3" />
              <p className="text-[hsl(var(--text-secondary))] font-medium text-sm">No standup updates for {monthNames[selectedMonth]}</p>
            </div>
          ) : weeklyGroups.map(([weekNum, entries]) => {
            const isExpanded = expandedWeeks[weekNum] !== false;
            return (
              <div key={weekNum} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <button onClick={() => setExpandedWeeks(prev => ({ ...prev, [weekNum]: !prev[weekNum] }))} className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-[hsl(var(--surface))]/50 transition-colors">
                  <div className="flex items-center gap-2">
                    {isExpanded ? <ChevronDown size={14} className="text-indigo-400" /> : <ChevronRight size={14} className="text-[hsl(var(--text-muted))]" />}
                    <Calendar size={14} className="text-indigo-400" />
                    <span className="font-semibold text-xs sm:text-sm">{getWeekLabel(Number(weekNum), selectedMonth + 1, selectedYear)}</span>
                  </div>
                  <span className="text-[10px] text-[hsl(var(--text-muted))]">{entries.length} updates</span>
                </button>

                {isExpanded && (
                  <div className="border-t border-[hsl(var(--border))] p-3 sm:p-4 space-y-3">
                    {entries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(s => {
                      const status = standupStatuses[s.status || 'in_progress'] || standupStatuses.in_progress;
                      const StatusIcon = status.icon;
                      const dept = getDeptInfo(s.department);
                      const isEditing = editingId === s.id;
                      const canEdit = canManage(s);

                      return (
                        <div key={s.id} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-3 sm:p-4 hover:border-indigo-500/20 transition-all">
                          {/* Header */}
                          <div className="flex items-center gap-2 mb-3">
                            {s.user_avatar ? <img src={s.user_avatar} alt="" className="w-7 h-7 rounded-full object-cover" /> : <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white text-[10px] font-bold">{s.user_name?.charAt(0)}</div>}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-medium text-xs">{s.user_name}</span>
                                <Badge className={`${dept.color}/10 text-[8px] px-1`}>{dept.label}</Badge>
                              </div>
                              <span className="text-[9px] text-[hsl(var(--text-muted))]">{new Date(s.created_at).toLocaleDateString('en-MY', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                            </div>
                            {canEdit && (
                              <Select value={s.status || 'in_progress'} onValueChange={(v) => handleUpdateStatus(s.id, v)}>
                                <SelectTrigger className={`w-[110px] h-7 text-[10px] ${status.bg} ${status.text} border ${status.border}`}>
                                  <StatusIcon size={10} className="mr-1" /><SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                                  <SelectItem value="pending" className="text-amber-400 text-xs">Pending</SelectItem>
                                  <SelectItem value="in_progress" className="text-blue-400 text-xs">In Progress</SelectItem>
                                  <SelectItem value="completed" className="text-emerald-400 text-xs">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            {canEdit && (
                              <button onClick={() => setEditingId(isEditing ? null : s.id)} className={`p-1 rounded transition-colors ${isEditing ? 'text-indigo-400 bg-indigo-500/10' : 'text-[#6b7280] hover:text-white'}`}>
                                <Edit2 size={12} />
                              </button>
                            )}
                          </div>

                          {/* Points Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {[
                              { key: 'done', label: 'Done', icon: CheckCircle, color: '#10b981', borderClass: 'border-emerald-500/15', bgClass: 'bg-emerald-500/5' },
                              { key: 'doing', label: 'Doing', icon: Clock, color: '#3b82f6', borderClass: 'border-blue-500/15', bgClass: 'bg-blue-500/5' },
                              { key: 'blockers', label: 'Blockers', icon: AlertTriangle, color: '#ef4444', borderClass: 'border-red-500/15', bgClass: 'bg-red-500/5' },
                            ].map(section => {
                              const SIcon = section.icon;
                              const pts = toPointsArray(s[section.key]);
                              return (
                                <div key={section.key} className={`${section.bgClass} border ${section.borderClass} rounded-lg p-2.5`}>
                                  <div className="flex items-center gap-1.5 text-[10px] font-semibold mb-2" style={{ color: section.color }}>
                                    <SIcon size={11} /> {section.label}
                                    <span className="text-[8px] text-[#6b7280] font-normal ml-auto">{pts.length} points</span>
                                  </div>
                                  <PointsDisplay
                                    points={s[section.key]}
                                    color={section.color}
                                    emptyText={section.key === 'blockers' ? 'No blockers' : 'No items'}
                                  />
                                  {isEditing && (
                                    <button onClick={async () => {
                                      const text = prompt(`Add ${section.label} point:`);
                                      if (text?.trim()) {
                                        const newPts = [...pts, { id: `new-${Date.now()}`, text: text.trim() }];
                                        await handleUpdatePoints(s.id, section.key, newPts);
                                      }
                                    }} className="flex items-center gap-1 text-[9px] mt-2 px-2 py-1 rounded-md border border-dashed hover:bg-white/5 transition-colors" style={{ color: section.color, borderColor: `${section.color}40` }}>
                                      <Plus size={10} /> Add point
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add Standup Dialog */}
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><ClipboardList size={16} className="text-indigo-400" /> Post Standup Update</DialogTitle></DialogHeader>
            <div className="space-y-4 py-3">
              <div>
                <label className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 flex items-center gap-1.5"><Briefcase size={12} /> Department *</label>
                <Select value={form.department} onValueChange={(v) => setForm({...form, department: v})}>
                  <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-sm">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                    {departments.filter(d => d.id !== 'all').map(dept => (
                      <SelectItem key={dept.id} value={dept.id}><span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${dept.color}`} />{dept.label}</span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#10b981] mb-2 flex items-center gap-1.5"><CheckCircle size={12} /> What did you accomplish?</label>
                <PointsInput points={form.done} onChange={(p) => setForm({...form, done: p})} color="#10b981" placeholder="Completed task..." />
              </div>
              <div>
                <label className="text-xs font-medium text-[#3b82f6] mb-2 flex items-center gap-1.5"><Clock size={12} /> What are you working on?</label>
                <PointsInput points={form.doing} onChange={(p) => setForm({...form, doing: p})} color="#3b82f6" placeholder="Current task..." />
              </div>
              <div>
                <label className="text-xs font-medium text-[#ef4444] mb-2 flex items-center gap-1.5"><AlertTriangle size={12} /> Any blockers?</label>
                <PointsInput points={form.blockers} onChange={(p) => setForm({...form, blockers: p})} color="#ef4444" placeholder="Issue or dependency..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdd(false)} size="sm">Cancel</Button>
              <Button onClick={handleAdd} disabled={submitting} className="bg-gradient-to-r from-indigo-500 to-violet-600 text-white" size="sm">
                {submitting && <Loader2 size={14} className="animate-spin mr-1.5" />}Post
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Summary Report Modal */}
        <Dialog open={reportOpen} onOpenChange={setReportOpen}>
          <DialogContent className="sm:max-w-2xl bg-[#0a0a0f] border-[#1a1a25] text-white max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2.5 text-base font-bold">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center"><BarChart3 size={13} className="text-white" /></div>
                Standup Report
              </DialogTitle>
            </DialogHeader>

            {(() => {
              // Filter standups by selected week
              const reportStandups = reportWeek === 'all' ? filteredStandups : filteredStandups.filter(s => String(getWeekInfo(s.created_at)) === reportWeek);
              
              // Calculate stats for filtered data
              let rTotalPoints = 0, rDoneCount = 0, rDoingCount = 0, rBlockerCount = 0;
              reportStandups.forEach(s => {
                rDoneCount += toPointsArray(s.done).length;
                rDoingCount += toPointsArray(s.doing).length;
                rBlockerCount += toPointsArray(s.blockers).length;
              });
              rTotalPoints = rDoneCount + rDoingCount + rBlockerCount;
              const rMembers = new Set(reportStandups.map(s => s.user_id)).size;

              // Weekly breakdown for chart
              const weeklyData = [1,2,3,4,5].map(w => {
                const ws = filteredStandups.filter(s => getWeekInfo(s.created_at) === w);
                let done = 0, doing = 0, blockers = 0;
                ws.forEach(s => { done += toPointsArray(s.done).length; doing += toPointsArray(s.doing).length; blockers += toPointsArray(s.blockers).length; });
                return { week: `W${w}`, updates: ws.length, done, doing, blockers, total: done + doing + blockers, members: new Set(ws.map(s => s.user_id)).size };
              }).filter(w => w.updates > 0);

              // Department breakdown
              const deptData = {};
              reportStandups.forEach(s => {
                const d = s.department || 'unknown';
                if (!deptData[d]) deptData[d] = { updates: 0, members: new Set(), done: 0, doing: 0, blockers: 0 };
                deptData[d].updates++;
                deptData[d].members.add(s.user_id);
                deptData[d].done += toPointsArray(s.done).length;
                deptData[d].doing += toPointsArray(s.doing).length;
                deptData[d].blockers += toPointsArray(s.blockers).length;
              });

              // Per-member data
              const memberData = {};
              reportStandups.forEach(s => {
                if (!memberData[s.user_id]) memberData[s.user_id] = { name: s.user_name, avatar: s.user_avatar, dept: s.department, updates: 0, done: 0, doing: 0, blockers: 0 };
                memberData[s.user_id].updates++;
                memberData[s.user_id].done += toPointsArray(s.done).length;
                memberData[s.user_id].doing += toPointsArray(s.doing).length;
                memberData[s.user_id].blockers += toPointsArray(s.blockers).length;
              });

              const maxWeekPoints = Math.max(...weeklyData.map(w => w.total), 1);

              return (
                <div className="space-y-5">
                  {/* Period Selector — always show W1-W4/W5 */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button onClick={() => setReportWeek('all')}
                      className={`px-3 py-1.5 rounded text-[9px] font-bold uppercase tracking-widest transition-all ${reportWeek === 'all' ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20' : 'bg-[#111118] text-[#6b7280] border border-[#1a1a25] hover:text-white'}`}
                    >All</button>
                    {(() => {
                      const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
                      const totalWeeks = Math.ceil(daysInMonth / 7);
                      return Array.from({ length: totalWeeks }, (_, i) => i + 1).map(w => {
                        const startDay = (w - 1) * 7 + 1;
                        const endDay = Math.min(w * 7, daysInMonth);
                        const hasData = filteredStandups.some(s => getWeekInfo(s.created_at) === w);
                        return (
                          <button key={w} onClick={() => setReportWeek(String(w))}
                            className={`px-3 py-1.5 rounded text-[9px] font-bold uppercase tracking-widest transition-all ${reportWeek === String(w) ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20' : `bg-[#111118] ${hasData ? 'text-[#9ca3af]' : 'text-[#3a3a45]'} border border-[#1a1a25] hover:text-white`}`}
                          >W{w} <span className="text-[7px] font-normal opacity-60">({startDay}-{endDay})</span></button>
                        );
                      });
                    })()}
                  </div>

                  {/* Header */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-500/10 via-[#0a0a0f] to-violet-600/10 border border-indigo-500/20">
                    <div className="text-[8px] font-bold uppercase tracking-[0.3em] text-indigo-400 mb-1">Weekly Standup Report</div>
                    <h3 className="text-sm font-bold text-white">{monthNames[selectedMonth]} {selectedYear} {reportWeek !== 'all' ? `— Week ${reportWeek}` : ''}</h3>
                  </div>

                  {/* KPI Cards */}
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {[
                      { label: 'Updates', val: reportStandups.length, color: '#8b5cf6' },
                      { label: 'Members', val: rMembers, color: '#3b82f6' },
                      { label: 'Done', val: rDoneCount, color: '#10b981' },
                      { label: 'Doing', val: rDoingCount, color: '#3b82f6' },
                      { label: 'Blockers', val: rBlockerCount, color: '#ef4444' },
                      { label: 'Total Pts', val: rTotalPoints, color: '#f59e0b' },
                    ].map(m => (
                      <div key={m.label} className="p-2.5 rounded-xl bg-[#111118] border border-[#1a1a25] text-center">
                        <div className="text-[7px] font-bold uppercase tracking-widest text-[#6b7280]">{m.label}</div>
                        <div className="text-base font-bold font-mono mt-0.5" style={{ color: m.color }}>{m.val}</div>
                      </div>
                    ))}
                  </div>

                  {/* Weekly Activity Chart */}
                  {reportWeek === 'all' && weeklyData.length > 1 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                        <span className="text-[8px] font-bold uppercase tracking-[0.25em] text-[#6b7280]">Weekly Activity</span>
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                      </div>
                      <div className="p-4 rounded-xl bg-[#111118] border border-[#1a1a25]">
                        <div className="flex items-end gap-3 h-24">
                          {weeklyData.map(w => (
                            <div key={w.week} className="flex-1 flex flex-col items-center gap-1">
                              <div className="w-full flex flex-col items-center" style={{ height: '80px' }}>
                                {/* Stacked bar */}
                                <div className="w-full flex flex-col-reverse items-center flex-1 justify-end">
                                  <div className="w-8 rounded-t" style={{ height: `${(w.done / maxWeekPoints) * 70}px`, background: '#10b981' }} />
                                  <div className="w-8" style={{ height: `${(w.doing / maxWeekPoints) * 70}px`, background: '#3b82f6' }} />
                                  <div className="w-8 rounded-b" style={{ height: `${(w.blockers / maxWeekPoints) * 70}px`, background: '#ef4444' }} />
                                </div>
                              </div>
                              <span className="text-[9px] font-bold text-[#9ca3af]">{w.week}</span>
                              <span className="text-[8px] text-[#6b7280]">{w.updates}u</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-4 mt-3 justify-center text-[8px]">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#10b981]" />Done</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#3b82f6]" />Doing</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#ef4444]" />Blockers</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Department Breakdown */}
                  {Object.keys(deptData).length > 1 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                        <span className="text-[8px] font-bold uppercase tracking-[0.25em] text-[#6b7280]">By Department</span>
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                      </div>
                      <div className="space-y-1.5">
                        {Object.entries(deptData).sort(([,a],[,b]) => b.updates - a.updates).map(([deptId, d]) => {
                          const dept = getDeptInfo(deptId);
                          const total = d.done + d.doing + d.blockers;
                          return (
                            <div key={deptId} className="flex items-center gap-2 p-2.5 rounded-lg bg-[#111118] border border-[#1a1a25]">
                              <span className={`w-2 h-2 rounded-full ${dept.color} shrink-0`} />
                              <span className="text-[10px] font-medium text-white w-20 truncate">{dept.label}</span>
                              <div className="flex-1 h-3 bg-[#1a1a25] rounded-full overflow-hidden flex">
                                <div className="h-full bg-[#10b981]" style={{ width: `${total > 0 ? (d.done / total * 100) : 0}%` }} />
                                <div className="h-full bg-[#3b82f6]" style={{ width: `${total > 0 ? (d.doing / total * 100) : 0}%` }} />
                                <div className="h-full bg-[#ef4444]" style={{ width: `${total > 0 ? (d.blockers / total * 100) : 0}%` }} />
                              </div>
                              <span className="text-[9px] text-[#9ca3af] w-16 text-right">{d.updates}u | {d.members.size}m</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Per-member breakdown */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                      <span className="text-[8px] font-bold uppercase tracking-[0.25em] text-[#6b7280]">Member Submissions</span>
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#1a1a25] to-transparent" />
                    </div>
                    <div className="space-y-1.5">
                      {Object.values(memberData).sort((a, b) => b.updates - a.updates).map(m => {
                        const dept = getDeptInfo(m.dept);
                        const total = m.done + m.doing + m.blockers;
                        return (
                          <div key={m.name} className="flex items-center gap-2 p-2.5 rounded-lg bg-[#111118] border border-[#1a1a25]">
                            {m.avatar ? <img src={m.avatar} alt="" className="w-6 h-6 rounded-full shrink-0" /> : <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white text-[8px] font-bold shrink-0">{m.name?.charAt(0)}</div>}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-medium text-white truncate">{m.name}</span>
                                <Badge className={`${dept.color}/10 text-[7px] px-1`}>{dept.label}</Badge>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 h-1.5 bg-[#1a1a25] rounded-full overflow-hidden flex">
                                  <div className="h-full bg-[#10b981]" style={{ width: `${total > 0 ? (m.done / total * 100) : 0}%` }} />
                                  <div className="h-full bg-[#3b82f6]" style={{ width: `${total > 0 ? (m.doing / total * 100) : 0}%` }} />
                                  <div className="h-full bg-[#ef4444]" style={{ width: `${total > 0 ? (m.blockers / total * 100) : 0}%` }} />
                                </div>
                                <span className="text-[8px] text-[#6b7280] shrink-0">{m.updates}u | {total}pts</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </ResponsiveLayout>
  );
}
